import ExtendedSlider from '../../../components/slider/ExtendedSlider'
import { CalculatorPrimary } from '../../../prismic/prismicTypes'
import { MaxLoanToPropertyValue, MinPrincipalAmount } from '../../../utils/Constants'
import { TranslatedStrings } from '../../../utils/translations/TranslatedStrings'
import { TranslatedTextHtml } from '../../../utils/translations/TranslatedTextHtml'
import { formatNumberIS } from '../../../utils/Utils'
import { GetMortgageSettingsResponse, LoanCalculationInputs, LoanTypeEnumCamelCase } from '../newCalculator.models'

import styles from './newCalculatorContainers.module.scss'

type INewCalculatorHeaderProps = {
	loanAmount: number
	loanTerm?: number
	proprtyMarketValue: number
	calculatorPrismicTooltipData: CalculatorPrimary
	defaultOptions?: GetMortgageSettingsResponse | null
	onInputValueChange: (value: number | boolean, propertyName: keyof LoanCalculationInputs) => void
	onMultipleInputValueChange: (
		firstChange: {
			value: number | boolean
			propertyName: keyof LoanCalculationInputs
		},
		secondChange: {
			value: number | boolean
			propertyName: keyof LoanCalculationInputs
		}
	) => void
}

const NewCalculatorHeader = (props: INewCalculatorHeaderProps) => {
	const {
		loanAmount,
		proprtyMarketValue,
		calculatorPrismicTooltipData,
		loanTerm,
		defaultOptions,
		onInputValueChange,
		onMultipleInputValueChange,
	} = props

	const ltv = proprtyMarketValue > 0 ? (loanAmount / proprtyMarketValue) * 100 : 0

	// Use ltvMaxThreshold/ltvBaseThreshold from settings when available; fall back to the hardcoded constant.
	// Header always renders with AssetFormation values so we match on that product type.
	const assetFormationRates = defaultOptions?.interestRates.find(
		(x) => x.productType === LoanTypeEnumCamelCase.AssetFormation
	)
	const maxLtvRatio = (assetFormationRates?.ltvMaxThreshold ?? MaxLoanToPropertyValue * 100) / 100
	const minPrincipal = defaultOptions?.options.minPrincipalAmount ?? MinPrincipalAmount

	// Loan-amount slider bounds. Min is the absolute mortgage floor (no LTV-based floor —
	// user can go all the way down). Max is `proprtyMarketValue * maxLtvRatio` — LTV is
	// the only cap, no separate absolute maxPrincipalAmount cap. Floored to the slider step
	// so the native range input can actually reach it.
	const loanAmountStep = 100_000
	const loanAmountSliderMaxRaw = proprtyMarketValue
		? proprtyMarketValue * maxLtvRatio
		: (defaultOptions?.options?.maxPrincipalAmount ?? 195_000_000)
	const loanAmountSliderMax = Math.floor(loanAmountSliderMaxRaw / loanAmountStep) * loanAmountStep
	const ltvTooltip = assetFormationRates
		? TranslatedTextHtml.is.calculator.ltvTooltip({
				maxLtv: assetFormationRates.ltvMaxThreshold,
				baseLtv: assetFormationRates.ltvBaseThreshold,
				surchargePerPoint: assetFormationRates.ltvSurchargePerPoint,
			})
		: undefined

	// Preserve current LTV ratio when market value changes, capped at maxLtvRatio. No min-LTV
	// floor — a low LTV is preserved. Then clamp the resulting loan to [minPrincipal, value*maxLtvRatio].
	const handlePropertyMarketValueChange = (value: number) => {
		const currentRatio = proprtyMarketValue > 0 ? loanAmount / proprtyMarketValue : maxLtvRatio
		const cappedRatio = Math.min(currentRatio, maxLtvRatio)
		const proposed = Math.round((cappedRatio * value) / loanAmountStep) * loanAmountStep
		const newLoanAmount = Math.max(proposed, minPrincipal)
		onMultipleInputValueChange(
			{ value, propertyName: 'propertyMarketValueAmount' },
			{ value: newLoanAmount, propertyName: 'loanAmount' }
		)
	}

	return (
		<div className={styles.header}>
			<h1 className={styles.title}>{TranslatedStrings.is.newCalculator.headerTitle}</h1>
			<div className={styles.headerRow}>
				<ExtendedSlider
					value={loanAmount}
					min={minPrincipal}
					max={loanAmountSliderMax}
					label={TranslatedStrings.is.newCalculator.loanAmount}
					tooltip={ltvTooltip}
					minLabelFunction={(value) => `${formatNumberIS(value)} kr.`}
					middleLabelText={ltv > 0 ? `${ltv.toFixed(1).replace('.', ',')}%` : undefined}
					middleLabelTooltip={ltvTooltip}
					onChange={(value) => onInputValueChange(value, 'loanAmount')}
					valueSuffix=' kr.'
					step={loanAmountStep}
				/>
				<ExtendedSlider
					value={proprtyMarketValue}
					min={minPrincipal * 2}
					max={(defaultOptions?.options.maxPrincipalAmount ?? 195_000_000) * 2}
					label={TranslatedStrings.is.newCalculator.proprtyMarketValue}
					tooltip={
						calculatorPrismicTooltipData.property_market_value_tooltip ??
						TranslatedStrings.is.newCalculator.deafultPropertyMarketValueTooltip
					}
					onChange={handlePropertyMarketValueChange}
					minLabelFunction={(value) => `${formatNumberIS(value)} kr.`}
					valueSuffix=' kr.'
					step={loanAmountStep}
				/>
				<ExtendedSlider
					value={loanTerm ?? 40}
					min={defaultOptions?.options?.minLoanLengthMonths ? defaultOptions.options.minLoanLengthMonths / 12 : 5}
					max={defaultOptions?.options?.maxLoanLengthMonths ? defaultOptions.options.maxLoanLengthMonths / 12 : 40}
					label={TranslatedStrings.is.newCalculator.loanTerm}
					onChange={(value) => onInputValueChange(value, 'numberOfPaymentsMonths')}
					valueSuffix=' ár'
					step={1}
					minLabelFunction={(value) => `${TranslatedStrings.is.newCalculator.minimum} ${value} ár`}
					maxLabelFunction={(value) => `${TranslatedStrings.is.newCalculator.maximum} ${value} ár`}
					tooltip={
						calculatorPrismicTooltipData.loan_term_tooltip ?? TranslatedStrings.is.newCalculator.defaultLoanTermTooltip
					}
				/>
			</div>
		</div>
	)
}

export default NewCalculatorHeader
